import axios from 'axios';
import { API_CONFIG } from './config';
import tokenManager from './tokenManager';
import refreshManager from './refreshManager';
import offlineQueue from './offlineQueue';

// ---------------------------------------------------------------------------
// Inactivity tracking
// Tracks the last time a non-auth request was made. If the gap exceeds the
// configured threshold the session is treated as expired on the next 401.
// ---------------------------------------------------------------------------
let lastActivity = Date.now();

const updateActivity = () => { lastActivity = Date.now(); };
const isInactive = () => Date.now() - lastActivity > API_CONFIG.inactivityTimeout;

// ---------------------------------------------------------------------------
// Public routes that must never trigger a token refresh or redirect to /login.
// ---------------------------------------------------------------------------
const PUBLIC_PATH_PREFIXES = [
  '/home', '/features', '/pricing', '/about', '/faqs', '/contact',
  '/demo', '/privacy', '/terms', '/security', '/api-docs',
  '/partner', '/cookie-policy', '/dpa', '/blog',
  '/register', '/login', '/forgot-password', '/stepwise-register'
];
const isPublicPath = () =>
  PUBLIC_PATH_PREFIXES.some((p) => window.location.pathname.startsWith(p));

// ---------------------------------------------------------------------------
// Axios instance
// withCredentials: true  â†’ sends the httpOnly refresh-token cookie on every
//                          request, including the /auth/refresh call made by
//                          refreshManager. The access token travels in the
//                          Authorization header (in-memory only, never stored).
// ---------------------------------------------------------------------------
const api = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: { 'X-Api-Source': 'ui' },
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// REQUEST interceptor
// Single responsibility: attach the in-memory access token when present.
// Never reads localStorage â€” that was the root cause of the stale-token bug.
// ---------------------------------------------------------------------------
api.interceptors.request.use(
  (config) => {
    if (!config.url.includes('/auth/')) updateActivity();

    const token = tokenManager.get();
    if (token) {
      // Only set if not already explicitly overridden by the caller.
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Explicitly add CSRF token header
    // This fixes CSRF token missing errors (e.g. on DELETE) when Axios drops
    // the X-XSRF-TOKEN header due to absolute baseURLs / cross-origin checks.
    if (typeof document !== 'undefined' && document.cookie) {
      const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
      if (match && match[1]) {
        config.headers['X-XSRF-TOKEN'] = decodeURIComponent(match[1]);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------------------------------------
// RESPONSE interceptor
// Handles 401s with a single, race-condition-free refresh flow:
//   1. Mark the request as retried (_retry flag) to prevent infinite loops.
//   2. Delegate to refreshManager.refresh() â€” it guarantees only one
//      /auth/refresh call is in-flight regardless of how many 401s arrive.
//   3. On success: attach the new token and replay the original request.
//   4. On failure: wipe the token and redirect to /login.
//
// Offline handling:
//   Network errors (no response) are queued in offlineQueue. The online
//   event listener below drains the queue when connectivity is restored.
// ---------------------------------------------------------------------------
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Guard: network/setup errors have no config
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // --- Subscription Restriction Handling (Limits, Expirations, Features) ---
    const subCodes = ['SUBSCRIPTION_EXPIRED', 'LIMIT_REACHED', 'FEATURE_NOT_AVAILABLE', 'PLAN_UPGRADE_REQUIRED'];
    if ((error.response?.status === 402 || error.response?.status === 403) && subCodes.includes(error.response?.data?.code)) {
      const code = error.response.data?.code;

      // FEATURE_NOT_AVAILABLE on GET requests = background/automatic query hitting a gated endpoint.
      // Silently reject without dispatching the modal â€” the UI should hide these widgets via plan checks.
      // The modal is only shown when a user explicitly navigates to or acts within a restricted module.
      if (code === 'FEATURE_NOT_AVAILABLE' && originalRequest.method?.toLowerCase() === 'get') {
        const silentError = new Error('Feature not available');
        silentError.isSilentRestriction = true;
        return Promise.reject(silentError);
      }

      // Dispatch event to open the centralized UpgradeModal / Overlay
      if (!isPublicPath()) {
        window.dispatchEvent(new CustomEvent('subscription-restriction', { detail: error.response.data }));
      }

      // If it's an expired error on a non-public path, handle silently (overlay handles it)
      if (error.response.status === 402 && !isPublicPath()) {
        const silentError = new Error('Subscription Restricted');
        silentError.isSilent = true;
        return Promise.reject(silentError);
      }

      // For 403s on non-public paths, handle silently
      if (error.response.status === 403 && !isPublicPath()) {
        const restrictedError = error;
        restrictedError.isSilentRestriction = true;
        return Promise.reject(restrictedError);
      }

      if (!navigator.onLine) {
        offlineQueue.enqueue(originalRequest);
      }

      // On public paths (like Login), let the error bubble up normally
      return Promise.reject(error);
    }

    // --- Concurrency / Optimistic Locking Conflict (409) ---
    if (error.response?.status === 409) {
      error.isConflict = true;
      return Promise.reject(error);
    }

    // --- Permission-specific 401 bypass ---
    // A small set of server error codes indicates the token is valid but the user
    // lacks permission for a specific resource. These must NOT trigger a refresh
    // or session-expiry dialog — let the error surface normally to the component.
    // (Per HTTP semantics, 403 is correct for this; we handle 401 defensively.)
    const PERMISSION_ONLY_401_CODES = [
      'INSUFFICIENT_PERMISSIONS', 'ACCESS_DENIED',
      'RESOURCE_FORBIDDEN', 'COMPANY_ACCESS_DENIED',
    ];
    if (
      error.response?.status === 401 &&
      PERMISSION_ONLY_401_CODES.includes(error.response?.data?.code)
    ) {
      return Promise.reject(error);
    }

    // --- 401 handling ---
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Never attempt refresh on public pages or auth endpoints themselves.
      if (isPublicPath() || originalRequest.url.includes('/auth/')) {
        return Promise.reject(error);
      }

      // Treat long inactivity as an expired session â€” skip refresh.
      if (isInactive()) {
        refreshManager.handleAuthFailure('inactivity');
        return Promise.reject(new Error('Session expired due to inactivity'));
      }

      // Mark so this request is only retried once.
      originalRequest._retry = true;

      try {
        // refreshManager.refresh() is idempotent: concurrent callers all
        // await the same promise, so /auth/refresh is called exactly once.
        const newToken = await refreshManager.refresh();

        // Attach the fresh token to the retried request.
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Distinguish three distinct failure modes:
        //
        //  1. Auth-specific HTTP error (400 / 401 / 403) from /auth/refresh:
        //     The server definitively rejected the refresh token. Clear the
        //     session and show the expiry dialog.
        //
        //  2. Server error (5xx) from /auth/refresh:
        //     The refresh endpoint itself had a problem (deploy, DB hiccup).
        //     The refresh token is NOT proven invalid. Do NOT show the dialog;
        //     propagate a connectivity error so the component can surface it.
        //
        //  3. No response (network timeout / offline):
        //     Cannot confirm the session is invalid. Preserve auth state and
        //     propagate a connectivity error. The offline queue handles queuing.
        //
        const status = refreshError.response?.status;
        const isDefinitiveAuthFailure = status === 400 || status === 401 || status === 403;

        if (isDefinitiveAuthFailure) {
          refreshManager.handleAuthFailure('expired');
        } else if (refreshError.response) {
          // 5xx or other unexpected status — treat as server unavailable
          refreshError.message = 'Authentication server error. Please try again shortly.';
        }
        // No response → network issue, message already set by offline handler below

        return Promise.reject(refreshError);
      }
    }

    // --- Network / offline handling ---
    if (!error.response) {
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        error.message = 'Request timeout. Server may be slow or unavailable.';
      } else if (!navigator.onLine) {
        // Device is offline â€” queue the request for later replay.
        return offlineQueue.enqueue(originalRequest);
      } else {
        error.message = 'Cannot connect to server. Please check your connection.';
      }
    }

    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Offline â†’ online recovery
// When the browser regains connectivity, drain any queued requests.
// A fresh access token will be attached by the request interceptor above.
// ---------------------------------------------------------------------------
window.addEventListener('online', () => {
  if (offlineQueue.size() > 0) {
    offlineQueue.drain(api);
  }
});

// ---------------------------------------------------------------------------
// Idempotency Helpers
// ---------------------------------------------------------------------------
export const generateIdempotencyKey = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  verify: () => api.get('/auth/verify'),
  // Refresh token is in the httpOnly cookie â€” no body needed.
  refresh: () => api.post('/auth/refresh', {}),
  changePassword: (passwordData) => api.post('/auth/change-password', passwordData),
  forgotPassword: (email) => api.post('/auth/forgot-password', typeof email === 'string' ? { email } : email),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// MFA API
export const mfaAPI = {
  getStatus: () => api.get('/auth/mfa/status'),
  setup: () => api.post('/auth/mfa/setup'),
  verifySetup: (token) => api.post('/auth/mfa/verify-setup', { token }),
  verifyLogin: (data) => api.post('/auth/mfa/verify', data),
  disable: (data) => api.post('/auth/mfa/disable', data),
  regenerateCodes: (token) => api.post('/auth/mfa/backup-codes', { token }),
};

// Users API
export const usersAPI = {
  getAll: () => api.get('/users'),
  getById: (id) => api.get(`/users/${id}`),
  create: (userData) => api.post('/users', userData),
  update: (id, userData) => api.put(`/users/${id}`, userData),
  delete: (id, hardDelete = false) => api.delete(`/users/${id}${hardDelete ? '?hardDelete=true' : ''}`),
};

// Privacy API
export const privacyAPI = {
  exportData: () => api.get('/privacy/export', { responseType: 'blob' }),
};

// Companies API
export const companiesAPI = {
  getCurrent: () => api.get('/companies/current'),
  create: (companyData) => api.post('/companies', companyData),
  update: (...args) => {
    // If only one argument, it's definitely a context-based update (current company)
    if (args.length === 1) {
      return api.put('/companies', args[0]);
    }
    // Two arguments: (id, data)
    if (args.length >= 2) {
      const [id, data] = args;
      // Safety: if ID is an object or weird string, fallback to context update
      if (typeof id === 'object' || id === '[object Object]') {
        return api.put('/companies', id);
      }
      return api.put(`/companies/${id}`, data);
    }
    return api.put('/companies', args[0]);
  },
  getOwnerships: () => api.get('/companies/consolidation/ownerships'),
  createOwnership: (data) => api.post('/companies/consolidation/ownerships', data),
  getDashboard: (params) => api.get('/companies/dashboard', { params }),
  getAll: (params) => api.get('/companies', { params }), // Updated to use the endpoint I just modified
  switch: (companyId) => api.post('/companies/switch', { companyId }),
  delete: (id) => api.delete(`/companies/${id}`),
  export: () => api.get('/companies/export', { responseType: 'blob' }),
  reset: (confirmText) => api.post('/companies/reset', { confirm: confirmText }),
};

// Chart of Accounts API
export const chartOfAccountsAPI = {
  getAll: (params) => api.get('/chart-of-accounts', { params }),
  getById: (id) => api.get(`/chart-of-accounts/${id}`),
  create: (accountData) => api.post('/chart-of-accounts', accountData),
  update: (id, accountData) => api.put(`/chart-of-accounts/${id}`, accountData),
  delete: (id) => api.delete(`/chart-of-accounts/${id}`),
  getTree: () => api.get('/chart-of-accounts/tree'),
  getBalances: (params) => api.get('/chart-of-accounts/balances', { params }),
  getAccountBalance: (id, params) => api.get(`/chart-of-accounts/${id}/balance`, { params }),
};

// Journal Entries API
export const journalEntriesAPI = {
  getAll: (params) => api.get('/journal-entries', { params }),
  getById: (id) => api.get(`/journal-entries/${id}`),
  create: (entryData) => api.post('/journal-entries', entryData),
  update: (id, entryData) => api.put(`/journal-entries/${id}`, entryData),
  delete: (id) => api.delete(`/journal-entries/${id}`),
  post: (id) => api.post(`/journal-entries/${id}/post`),
  bulkPost: (ids) => api.post('/journal-entries/bulk-post', { ids }),
  reverse: (id, data) => api.post(`/journal-entries/${id}/reverse`, data),
  peek: () => api.get('/numbering/peek/journal_entry'),
};

// Recurring Journal Entries API
export const recurringJournalEntriesAPI = {
  getAll: (params) => api.get('/recurring-journal-entries', { params }),
  getById: (id) => api.get(`/recurring-journal-entries/${id}`),
  create: (entryData) => api.post('/recurring-journal-entries', entryData),
  update: (id, entryData) => api.put(`/recurring-journal-entries/${id}`, entryData),
  delete: (id) => api.delete(`/recurring-journal-entries/${id}`),
  generate: (id, data) => api.post(`/recurring-journal-entries/${id}/generate`, data),
};

// Network connectivity check
const checkNetworkConnectivity = async () => {
  try {
    const response = await fetch(`${API_CONFIG.baseURL}/health`, {
      method: 'GET',
      signal: AbortSignal.timeout(5000)
    });
    return response.ok;
  } catch (error) {
    return false;
  }
};

// Sales Invoices API
export const invoicesAPI = {
  getAll: (params) => api.get('/invoices', { params }),
  getReturnable: (params) => api.get('/invoices/returnable', { params }),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (invoiceData) => api.post('/invoices', invoiceData),
  update: (id, invoiceData) => api.put(`/invoices/${id}`, invoiceData),
  delete: (id) => api.delete(`/invoices/${id}`),
  send: async (id) => {
    // Check network connectivity first
    const isConnected = await checkNetworkConnectivity();
    if (!isConnected) {
      throw new Error('Network connection unavailable. Please check your internet connection.');
    }
    return api.post(`/invoices/${id}/send`, {}, { timeout: 60000 });
  },
  markPaid: (id) => api.post(`/invoices/${id}/paid`),
  approve: (id) => api.post(`/invoices/${id}/approve`),
  approveAndPost: (id) => api.post(`/invoices/${id}/approve-and-post`),
  reject: (id, reason) => api.post(`/invoices/${id}/reject`, { reason }),
  remind: (id) => api.post(`/invoices/${id}/remind`),
  getSummary: (params) => api.get('/invoices/summary', { params }),
  returnItems: (id, returnData) => api.post(`/invoices/${id}/return`, returnData),
  void: (id, data) => api.post(`/invoices/${id}/void`, data),
  reverse: (id, data) => api.post(`/invoices/${id}/reverse`, data),
  getNextNumber: (type) => api.get('/invoices/next-number', { params: { type } }),
  peek: (type) => api.get(`/numbering/peek/${type || 'invoice'}`),
  uploadFile: (formData) => api.post('/invoices/upload-file', formData, { timeout: 60000 }),
  post: (id) => api.post(`/invoices/${id}/post`),
};

export { checkNetworkConnectivity };

// Inventory API
export const inventoryAPI = {
  getAll: (params) => api.get('/inventory/items', { params }),
  getById: (id) => api.get(`/inventory/items/${id}`),
  create: (itemData) => api.post('/inventory/items', itemData),
  update: (id, itemData) => api.put(`/inventory/items/${id}`, itemData),
  delete: (id) => api.delete(`/inventory/items/${id}`),
  adjustStock: (id, adjustmentData) => api.post(`/inventory/stock/adjustment/${id}`, adjustmentData),
  getAdjustments: (params) => api.get('/inventory/stock/adjustments', { params }),
  deleteAdjustment: (id) => api.delete(`/inventory/stock/adjustments/${id}`),
  updateAdjustment: (id, data) => api.put(`/inventory/stock/adjustments/${id}`, data),
  getLowStock: (threshold) => api.get('/inventory/stock/low-stock', { params: { threshold } }),
  getValuation: (method) => api.get('/inventory/stock/valuation', { params: { valuationMethod: method } }),
  getStockMovement: (params) => api.get('/inventory/stock/movement', { params }),
  createBatch: (batchData) => api.post('/inventory/batches', batchData),
  peek: (type) => api.get(`/numbering/peek/${type || 'inventory_item'}`),
  uploadImage: (id, formData) => api.post(`/inventory/items/${id}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000
  }),
  checkBarcode: (barcode, excludeItemId, excludePackagingId) =>
    api.get('/inventory/items/check-barcode', { params: { barcode, excludeItemId, excludePackagingId } }),
  lookupBarcode: (barcode) =>
    api.get(`/inventory/items/barcode/${encodeURIComponent(barcode)}`),
  evaluateReadiness: (payload) => api.post('/inventory/items/evaluate-readiness', payload),
  getReadiness: (id, params) => api.get(`/inventory/items/${id}/readiness`, { params }),
  getValidationRules: () => api.get('/inventory/items/validation-rules'),
  updateValidationRules: (rules) => api.put('/inventory/items/validation-rules', { rules }),
  // Stock adjustment approval workflow
  submitAdjustmentForApproval: (id, data) => api.post(`/inventory/stock/adjustments/${id}/submit-approval`, data),
  approveAdjustment: (id, data) => api.post(`/inventory/stock/adjustments/${id}/approve`, data),
  rejectAdjustment: (id, data) => api.post(`/inventory/stock/adjustments/${id}/reject`, data),
};

// Purchase Orders API
export const purchaseOrdersAPI = {
  getAll: (params) => api.get('/purchase-orders', { params }),
  getSummary: (params) => api.get('/purchase-orders/summary', { params }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data) => api.post('/purchase-orders', data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  receive: (id, receipts) => api.post(`/purchase-orders/${id}/receive`, { receipts }),
  convert: (id, data) => api.post(`/purchase-orders/${id}/convert`, data),
  approve: (id) => api.post(`/purchase-orders/${id}/approve`),
  reject: (id) => api.post(`/purchase-orders/${id}/reject`),
  peek: () => api.get('/numbering/peek/purchase_order'),
};

// Purchase Requisitions API
export const purchaseRequisitionsAPI = {
  getAll: (params) => api.get('/purchase-requisitions', { params }),
  getSummary: (params) => api.get('/purchase-requisitions/summary', { params }),
  getById: (id) => api.get(`/purchase-requisitions/${id}`),
  create: (data) => api.post('/purchase-requisitions', data),
  update: (id, data) => api.put(`/purchase-requisitions/${id}`, data),
  delete: (id) => api.delete(`/purchase-requisitions/${id}`),
  submit: (id) => api.post(`/purchase-requisitions/${id}/submit`),
  approve: (id) => api.post(`/purchase-requisitions/${id}/approve`),
  reject: (id) => api.post(`/purchase-requisitions/${id}/reject`),
  convert: (id, data) => {
    const payload = typeof data === 'string' ? { vendorId: data } : data;
    return api.post(`/purchase-requisitions/${id}/convert`, payload);
  },
  getConversionStatus: (id) => api.get(`/purchase-requisitions/${id}/conversion-status`),
  peek: () => api.get('/numbering/peek/purchase_requisition'),
};

// Stock Transfer Notes API (draft â†’ ship â†’ receive workflow)
export const stockTransfersAPI = {
  getAll: (params) => api.get('/stock-transfers', { params }),
  getById: (id) => api.get(`/stock-transfers/${id}`),
  create: (data) => api.post('/stock-transfers', data),
  ship: (id) => api.post(`/stock-transfers/${id}/ship`),
  receive: (id) => api.post(`/stock-transfers/${id}/receive`),
  peekNextNumber: () => api.get('/stock-transfers/next-number'),
};

// Warehouse API
export const warehouseAPI = {
  getAll: () => api.get('/warehouses'),
  getById: (id) => api.get(`/warehouses/${id}`),
  create: (warehouseData) => api.post('/warehouses', warehouseData),
  update: (id, warehouseData) => api.put(`/warehouses/${id}`, warehouseData),
  delete: (id) => api.delete(`/warehouses/${id}`),
  transferStock: (transferData) => api.post('/warehouses/stock/transfer', transferData),
  updateTransfer: (id, transferData) => api.put(`/warehouses/stock/transfer/${id}`, transferData),
  deleteTransfer: (id) => api.delete(`/warehouses/stock/transfer/${id}`),
  getStockByWarehouse: (warehouseId) => api.get(`/warehouses/stock/warehouse/${warehouseId}`),
  getTransfers: (params) => api.get('/warehouses/stock/transfers', { params }),
  getTransferPdf: (id) => api.get(`/warehouses/stock/transfer/${id}/pdf`, { responseType: 'blob' }),
};

// Tax API
export const taxAPI = {
  getAll: (params) => api.get('/tax-rates', { params }),
  getById: (id) => api.get(`/tax-rates/${id}`),
  create: (taxData) => api.post('/tax-rates', taxData),
  update: (id, taxData) => api.put(`/tax-rates/${id}`, taxData),
  delete: (id) => api.delete(`/tax-rates/${id}`),
  getTypes: () => api.get('/taxes/types/list'),
  calculate: (calculationData) => api.post('/taxes/calculate', calculationData),

  // Regulatory Credentials
  getCredentials: () => api.get('/taxes/credentials'),
  saveCredential: (data) => api.post('/taxes/credentials', data),
  deleteCredential: (id) => api.delete(`/taxes/credentials/${id}`),

  // ZATCA (Saudi Arabia)
  zatcaSubmitInvoice: (invoiceId) => api.post(`/taxes/zatca/einvoice/${invoiceId}`),

  // Qatar GTA / Dhareeba
  qatarPreviewVATReturn: (params) => api.get('/taxes/qatar/vat-return/preview', { params }),
  qatarSubmitVATReturn: (data) => api.post('/taxes/qatar/vat-return', data),
  qatarSubmitEInvoice: (invoiceId) => api.post(`/taxes/qatar/einvoice/${invoiceId}`),
};

export const reportsAPI = {
  // Report data fetching
  getTrialBalance: (params) => api.get('/reports/trial-balance', { params }),
  getProfitLoss: (params) => api.get('/reports/profit-loss', { params }),
  getBalanceSheet: (params) => api.get('/reports/balance-sheet', { params }),
  getEquityStatement: (params) => api.get('/reports/equity-statement', { params }),
  getCashFlow: (params) => api.get('/reports/cash-flow', { params }),
  getGeneralLedger: (params) => api.get('/reports/general-ledger', { params }),
  getAgedReceivables: (params) => api.get('/reports/aged-receivables', { params }),
  getAgedPayables: (params) => api.get('/reports/aged-payables', { params }),
  getInventoryStock: (params) => api.get('/reports/inventory/inventory-stock', { params }),
  getStockMovement: (params) => api.get('/reports/stock-movement/stock-movement', { params }),
  getStockValuation: (params) => api.get('/reports/stock-valuation/stock-valuation', { params }),
  getLowStock: (params) => api.get('/reports/low-stock/low-stock', { params }),
  getRevenueWaterfall: (params) => api.get('/reports/revenue-waterfall', { params }),
  getSegmentedProfitLoss: (params) => api.get('/reports/segmented-pl', { params }),
  getFixedAssetRegister: (params) => api.get('/reports/fixed-assets', { params }),
  getConsolidatedPL: (params) => api.get('/reports/consolidation/pl', { params }),
  getConsolidatedBS: (params) => api.get('/reports/consolidation/balance-sheet', { params }),
  getSnapshots: (params) => api.get('/reports/snapshots', { params }),
  createSnapshot: (data) => api.post('/reports/snapshots', data),
  getAIInsights: () => api.get('/reports/ai-insights'),
  getAIFinancialForecast: () => api.get('/reports/ai-forecasting/financial'),
  getAIInventoryForecast: () => api.get('/reports/ai-forecasting/inventory'),

  // Export methods
  exportTrialBalance: (format, params) => api.get(`/reports/trial-balance/export/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportBalanceSheet: (format, params) => api.get(`/reports/balance-sheet/export/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportEquityStatement: (format, params) => api.get(`/reports/equity-statement/export/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportProfitLoss: (format, params) => api.get(`/reports/profit-loss/export/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportCashFlow: (format, params) => api.get(`/reports/cash-flow/export/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportGeneralLedger: (format, params) => api.get(`/reports/general-ledger/export/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportAgedReceivables: (format, params) => api.get(`/reports/aged-receivables/export/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportAgedPayables: (format, params) => api.get(`/reports/aged-payables/export/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportInventoryStock: (format, params) => api.get(`/reports/inventory/export/inventory-stock/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportStockMovement: (format, params) => api.get(`/reports/stock-movement/export/stock-movement/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportStockValuation: (format, params) => api.get(`/reports/stock-valuation/export/stock-valuation/${format}`, {
    params,
    responseType: 'blob'
  }),
  exportLowStock: (format, params) => api.get(`/reports/low-stock/export/low-stock/${format}`, {
    params,
    responseType: 'blob'
  }),

  // Dropdown data for aged reports
  getAgedReceivablesCustomers: () => api.get('/reports/aged-receivables/customers'),
  getAgedPayablesVendors: () => api.get('/reports/aged-payables/vendors'),

  // GRNI Reports
  getGRNIAging: (params) => api.get('/reports/grni/aging', { params }),
  getGRNISummary: () => api.get('/reports/grni/summary'),
  getPOReconciliation: (poId) => api.get(`/reports/grni/po-reconciliation/${poId}`),
  getUninvoicedGRNLines: (poId, params) => api.get(`/reports/grni/uninvoiced/${poId}`, { params }),
  validateInvoiceGRNI: (data) => api.post('/reports/grni/validate-invoice', data),
};

// Customers API
export const customersAPI = {
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  create: (customerData) => api.post('/customers', customerData),
  update: (id, customerData) => api.put(`/customers/${id}`, customerData),
  delete: (id) => api.delete(`/customers/${id}`),
  getSummary: () => api.get('/customers/summary'),
  getNextCode: () => api.get('/customers/next-code'),
};

// Vendors API
export const vendorsAPI = {
  getAll: (params) => api.get('/vendors', { params }),
  getById: (id) => api.get(`/vendors/${id}`),
  create: (vendorData) => api.post('/vendors', vendorData),
  update: (id, vendorData) => api.put(`/vendors/${id}`, vendorData),
  delete: (id) => api.delete(`/vendors/${id}`),
  getSummary: () => api.get('/vendors/summary'),
  getNextCode: () => api.get('/vendors/next-code'),
};

// Customer Notifications API
export const customerNotificationsAPI = {
  getPreferences: (customerId) => api.get(`/customer-notifications/${customerId}/preferences`),
  updatePreferences: (customerId, preferences) => api.put(`/customer-notifications/${customerId}/preferences`, preferences),
  sendNotification: (customerId, notificationData) => api.post(`/customer-notifications/${customerId}/send-notification`, notificationData),
  getHistory: (customerId, params) => api.get(`/customer-notifications/${customerId}/history`, { params }),
  bulkSend: (bulkData) => api.post('/customer-notifications/bulk-send', bulkData),
};

// Vendor Notifications API
export const vendorNotificationsAPI = {
  getPreferences: (vendorId) => api.get(`/vendor-notifications/${vendorId}/preferences`),
  updatePreferences: (vendorId, preferences) => api.put(`/vendor-notifications/${vendorId}/preferences`, preferences),
  sendNotification: (vendorId, notificationData) => api.post(`/vendor-notifications/${vendorId}/send-notification`, notificationData),
  getHistory: (vendorId, params) => api.get(`/vendor-notifications/${vendorId}/history`, { params }),
  bulkSend: (bulkData) => api.post('/vendor-notifications/bulk-send', bulkData),
};

// Commission Agents API
export const commissionAgentsAPI = {
  getAll: (params) => api.get('/commission-agents', { params }),
  getById: (id) => api.get(`/commission-agents/${id}`),
  create: (agentData) => api.post('/commission-agents', agentData),
  update: (id, agentData) => api.put(`/commission-agents/${id}`, agentData),
  delete: (id) => api.delete(`/commission-agents/${id}`),
  getNextCode: () => api.get('/commission-agents/next-code'),
  getSummary: () => api.get('/commission-agents/summary'),
};

export const crmAPI = {
  getSummary: (params) => api.get('/crm/summary', { params }),
  getOpportunities: (params) => api.get('/crm/opportunities', { params }),
  createOpportunity: (data) => api.post('/crm/opportunities', data),
  getLeads: (params) => api.get('/crm/leads', { params }),
  getLeadById: (id) => api.get(`/crm/leads/${id}`),
  createLead: (data) => api.post('/crm/leads', data),
  updateLead: (id, data) => api.put(`/crm/leads/${id}`, data),
  convertLead: (id, data) => api.post(`/crm/leads/${id}/convert`, data),
  convertToOpportunity: (id, data) => api.post(`/crm/leads/${id}/convert-to-opportunity`, data),
};

export const hrAPI = {
  getLeaveRequests: (params) => api.get('/hr/leave/requests', { params }),
  createLeaveRequest: (data) => api.post('/hr/leave/requests', data),
  getLeaveTypes: () => api.get('/hr/leave/types'),
  getEmployeesList: () => api.get('/payroll/employees/list'),
};

// Roles API
export const rolesAPI = {
  getAll: (params) => api.get('/roles', { params }),
  getById: (id) => api.get(`/roles/${id}`),
  create: (roleData) => api.post('/roles', roleData),
  update: (id, roleData) => api.put(`/roles/${id}`, roleData),
  delete: (id) => api.delete(`/roles/${id}`),
  createDefaults: () => api.post('/roles/create-defaults'),
  syncPermissions: () => api.post('/roles/sync-permissions'),
};

// Item Categories API
export const itemCategoriesAPI = {
  getAll: (params) => api.get('/item-categories', { params }),
  getById: (id) => api.get(`/item-categories/${id}`),
  create: (categoryData) => api.post('/item-categories', categoryData),
  update: (id, categoryData) => api.put(`/item-categories/${id}`, categoryData),
  delete: (id) => api.delete(`/item-categories/${id}`),
  peek: () => api.get('/numbering/peek/item_category'),
};

// Units API
export const unitsAPI = {
  getAll: (params) => api.get('/units', { params }),
  getById: (id) => api.get(`/units/${id}`),
  create: (unitData) => api.post('/units', unitData),
  update: (id, unitData) => api.put(`/units/${id}`, unitData),
  delete: (id) => api.delete(`/units/${id}`),
};

// Payments API
export const paymentsAPI = {
  create: (paymentData) => {
    if (paymentData instanceof FormData) {
      return api.post('/payments', paymentData);
    }
    return api.post('/payments', paymentData);
  },
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  update: (id, paymentData) => {
    if (paymentData instanceof FormData) {
      return api.put(`/payments/${id}`, paymentData);
    }
    return api.put(`/payments/${id}`, paymentData);
  },
  uploadFile: (formData) => api.post('/payments/upload-file', formData, { timeout: 60000 }),
  delete: (id) => api.delete(`/payments/${id}`),
  getByInvoice: (invoiceId) => api.get(`/payments/invoice/${invoiceId}`),
  getByExpense: (expenseId) => api.get(`/payments/expense/${expenseId}`),
  getSummary: (params) => api.get('/payments/summary', { params }),
  reconcile: (id) => api.post(`/payments/${id}/reconcile`),
  getReceiptPdf: (id) => api.get(`/payments/${id}/receipt/pdf`, { responseType: 'blob' }),
  getGatewayIntent: (data) => api.post('/payments/gateway/intent', data),
  reverse: (id) => api.post(`/payments/${id}/reverse`),
  getNextNumber: () => api.get('/payments/next-number'),
  peek: () => api.get('/numbering/peek/payment'),
  bulkCreate: (data) => api.post('/payments/bulk', data),
};

// Shipments API
export const shipmentsAPI = {
  getAll: () => api.get('/warehouse/shipments'),
  getById: (id) => api.get(`/warehouse/shipments/${id}`),
  dispatch: (fulfillmentOrderId, data) => api.post(`/warehouse/fulfillment-orders/${fulfillmentOrderId}/dispatch`, data),
  update: (id, data) => api.put(`/warehouse/shipments/${id}`, data),
  submit: (id) => api.post(`/warehouse/shipments/${id}/submit`),
  approve: (id) => api.post(`/warehouse/shipments/${id}/approve`),
  notify: (id) => api.post(`/warehouse/shipments/${id}/notify`),
  uploadFile: (formData) => api.post('/warehouse/shipments/upload-file', formData, { timeout: 60000 }),
  downloadFile: (fileUrl) => api.get(fileUrl, { responseType: 'blob' }),
};

// Expenses API
export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getById: (id) => api.get(`/expenses/${id}`),
  getCategories: () => api.get('/expenses/categories'),
  create: (expenseData) => api.post('/expenses', expenseData),
  update: (id, expenseData) => api.put(`/expenses/${id}`, expenseData),
  uploadFile: (formData) => api.post('/expenses/upload-file', formData, { timeout: 60000 }),
  delete: (id) => api.delete(`/expenses/${id}`),
  void: (id) => api.post(`/expenses/${id}/void`),
  submit: (id) => api.post(`/expenses/${id}/submit`),
  cancel: (id, data) => api.post(`/expenses/${id}/cancel`, data),
  pendingPost: (id) => api.post(`/expenses/${id}/pending-post`),
  post: (id) => api.post(`/expenses/${id}/post`),
  markPaid: (id) => api.post(`/expenses/${id}/mark-paid`),
  reverse: (id, data) => api.post(`/expenses/${id}/reverse`, data),
  fail: (id, data) => api.post(`/expenses/${id}/fail`, data),

  getNextNumber: () => api.get('/expenses/next-number'),
  peek: () => api.get('/numbering/peek/expense'),
};

export const expenseCategoriesAPI = {
  getAll: (params) => api.get('/expense-categories', { params }),
  getById: (id) => api.get(`/expense-categories/${id}`),
  create: (data) => api.post('/expense-categories', data),
  update: (id, data) => api.put(`/expense-categories/${id}`, data),
  delete: (id) => api.delete(`/expense-categories/${id}`),
};

// Credit Notes API
export const creditNotesAPI = {
  getAll: (params) => api.get('/credit-notes', { params }),
  getById: (id) => api.get(`/credit-notes/${id}`),
  create: (data) => api.post('/credit-notes', data),
  update: (id, data) => api.put(`/credit-notes/${id}`, data),
  delete: (id) => api.delete(`/credit-notes/${id}`),
  apply: (id, data) => api.post(`/credit-notes/${id}/apply`, data),
  void: (id) => api.post(`/credit-notes/${id}/void`),
  getNextNumber: () => api.get('/credit-notes/next-number'),
  peek: () => api.get('/numbering/peek/credit_note'),
};

// Debit Notes API
export const debitNotesAPI = {
  getAll: (params) => api.get('/debit-notes', { params }),
  getById: (id) => api.get(`/debit-notes/${id}`),
  create: (data) => api.post('/debit-notes', data),
  update: (id, data) => api.put(`/debit-notes/${id}`, data),
  delete: (id) => api.delete(`/debit-notes/${id}`),
  apply: (id, data) => api.post(`/debit-notes/${id}/apply`, data),
  void: (id) => api.post(`/debit-notes/${id}/void`),
  getNextNumber: () => api.get('/debit-notes/next-number'),
  peek: () => api.get('/numbering/peek/debit_note'),
};

// Payroll API
export const payrollAPI = {
  getAll: (params) => api.get('/payroll', { params }),
  getById: (id) => api.get(`/payroll/${id}`),
  create: (data) => api.post('/payroll', data),
  update: (id, data) => api.put(`/payroll/${id}`, data),
  delete: (id) => api.delete(`/payroll/${id}`),
  // Named action methods â€” avoids the error-prone generic post(endpoint) builder
  process: (id) => api.post(`/payroll/${id}/process`),
  approve: (id) => api.post(`/payroll/${id}/approve`),
  pay: (id) => api.post(`/payroll/${id}/pay`),
  getSummary: (params) => api.get('/payroll/summary', { params }),
  getEmployeeSlips: (runId) => api.get(`/payroll/${runId}/employees`),
  getSlip: (runId, employeeId) => api.get(`/payroll/${runId}/slip/${employeeId}`),
  // Keep generic post for any other ad-hoc calls already in use
  post: (endpoint) => api.post(`/payroll/${endpoint}`),
  peek: () => api.get('/numbering/peek/payroll'),

  // WPS (Wage Protection System)
  generateWPS: (runId) => api.post(`/payroll/${runId}/wps`, {}, { responseType: 'blob' }),
  previewWPS: (runId) => api.get(`/payroll/${runId}/wps/preview`),

  // EOSB (End of Service Benefit)
  getEOSB: (employeeId, params) => api.get(`/payroll/eosb/${employeeId}`, { params }),
  settleEOSB: (employeeId, data) => api.post(`/payroll/eosb/${employeeId}/settle`, data),
  getEOSBProvision: (runId, params) => api.get(`/payroll/eosb/provision/${runId}`, { params }),
};

// Payroll Items API
export const payrollItemsAPI = {
  getAll: (params) => api.get('/payroll-items', { params }),
  getById: (id) => api.get(`/payroll-items/${id}`),
  create: (data) => api.post('/payroll-items', data),
  update: (id, data) => api.put(`/payroll-items/${id}`, data),
  delete: (id) => api.delete(`/payroll-items/${id}`),
};

// Quotations API
export const quotationsAPI = {
  getAll: (params) => api.get('/quotations', { params }),
  getById: (id) => api.get(`/quotations/${id}`),
  create: (data) => api.post('/quotations', data),
  update: (id, data) => api.put(`/quotations/${id}`, data),
  delete: (id) => api.delete(`/quotations/${id}`),
  convert: (id, options) => api.post(`/quotations/${id}/convert`, options),
  send: (id) => api.post(`/quotations/${id}/send`),
  getSummary: (params) => api.get('/quotations/summary', { params }),
  accept: (id) => api.post(`/quotations/${id}/accept`),
  reject: (id, reason) => api.post(`/quotations/${id}/reject`, { reason }),
  downloadPdf: async (id) => {
    const response = await api.get(`/quotations/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `quotation-${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  getNextNumber: () => api.get('/quotations/next-number'),
  peek: () => api.get('/numbering/peek/quotation'),
};

// Sales Orders API
export const salesOrdersAPI = {
  getAll: (params) => api.get('/sales-orders', { params }),
  getSummary: (params) => api.get('/sales-orders/summary', { params }),
  getById: (id) => api.get(`/sales-orders/${id}`),
  create: (data) => api.post('/sales-orders', data),
  update: (id, data) => api.put(`/sales-orders/${id}`, data),
  delete: (id) => api.delete(`/sales-orders/${id}`),
  confirm: (id) => api.post(`/sales-orders/${id}/confirm`),
  approve: (id) => api.post(`/sales-orders/${id}/approve`),
  reject: (id, reason) => api.post(`/sales-orders/${id}/reject`, { reason }),
  createFulfillment: (id, data) => api.post(`/sales-orders/${id}/fulfillment`, data),
  splitBackorder: (id) => api.post(`/sales-orders/${id}/split-backorder`),
  dispatchShipment: (salesOrderId, data) => api.post(`/sales-orders/${salesOrderId}/dispatch`, data),
  createInvoice: (id) => api.post(`/sales-orders/${id}/invoice`),
  peek: () => api.get('/numbering/peek/sales_order'),
};

// Purchase Invoices API
export const purchaseInvoicesAPI = {
  getAll: (params) => api.get('/purchase-invoices', { params }),
  getById: (id) => api.get(`/purchase-invoices/${id}`),
  create: (data) => api.post('/purchase-invoices', data),
  update: (id, data) => api.put(`/purchase-invoices/${id}`, data),
  delete: (id) => api.delete(`/purchase-invoices/${id}`),
  getSummary: (params) => api.get('/purchase-invoices/summary', { params }),
  approve: (id, data) => api.post(`/purchase-invoices/${id}/approve`, data || {}),
  approveAndPost: (id, data) => api.post(`/purchase-invoices/${id}/approve-and-post`, data || {}),
  reject: (id) => api.post(`/purchase-invoices/${id}/reject`),
  returnItems: (id, data) => api.post(`/purchase-invoices/${id}/return`, data),
  void: (id, data) => api.post(`/purchase-invoices/${id}/void`, data),
  reverse: (id) => api.post(`/purchase-invoices/${id}/reverse`),
  send: (id) => api.post(`/purchase-invoices/${id}/send`, {}),
  remind: (id) => api.post(`/purchase-invoices/${id}/remind`),
  peek: () => api.get('/numbering/peek/purchase_invoice'),
  post: (id) => api.post(`/purchase-invoices/${id}/post`),
  getMatchStatus: (id) => api.get(`/purchase-invoices/${id}/match-status`),
};

// Activity Logs API
export const activityLogsAPI = {
  getAll: (params) => api.get('/activity-logs', { params })
};

// Reconciliation API
export const reconciliationAPI = {
  uploadStatement: (statementData) => api.post('/reconciliation/upload', statementData),
  getStatements: () => api.get('/reconciliation'),
  getStatementLines: (statementId) => api.get(`/reconciliation/${statementId}/lines`),
  addStatementLine: (statementId, lineData) => api.post(`/reconciliation/${statementId}/lines`, lineData),
  reconcile: (reconciliationData) => api.post('/reconciliation/reconcile', reconciliationData),
};

// Location API for dynamic dropdowns
export const locationAPI = {
  getCountries: () => api.get('/locations/countries'),
  getStates: (countryCode) => api.get(`/locations/states/${countryCode}`),
  getCities: (countryCode, stateCode) => api.get(`/locations/cities/${countryCode}/${stateCode}`),
  getCitiesByCountry: (countryCode) => api.get(`/locations/cities/${countryCode}`),
};

// Notifications API
export const notificationsAPI = {
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  send: (data) => api.post('/notifications/send', data),
  getRecipients: (type) => api.get(`/notifications/recipients/${type}`),
  getHistory: (params) => api.get('/notifications/history', { params }),
  getTemplates: () => api.get('/notifications/templates'),
  updateTemplate: (type, data) => api.put(`/notifications/templates/${type}`, data)
};


// Admin API
export const adminAPI = {
  get: (endpoint) => api.get(`/admin${endpoint}`),
  post: (endpoint, data) => api.post(`/admin${endpoint}`, data),
  put: (endpoint, data) => api.put(`/admin${endpoint}`, data),
  delete: (endpoint) => api.delete(`/admin${endpoint}`),
};

// Super Admin API Extensions
export const superAdminAPI = {
  getLeads: (params) => api.get('/superadmin/leads', { params }),
  updateLead: (id, data) => api.put(`/superadmin/leads/${id}`, data),
  updateDemoLead: (id, data) => api.patch(`/superadmin/leads/${id}/demo`, data),
  deleteLead: (id) => api.delete(`/superadmin/leads/${id}`),
  grantTrial: (id) => api.post(`/superadmin/leads/${id}/grant-trial`),
  getContactMessages: (params) => api.get('/superadmin/contact-messages', { params }),
  getContactStats: () => api.get('/superadmin/contact-messages/stats'),
  updateContactStatus: (id, status) => api.put(`/superadmin/contact-messages/${id}/status`, { status }),
  triggerSweep: () => api.post('/superadmin/provisioning/sweep'),
  resetProvisioning: (id) => api.post(`/superadmin/leads/${id}/reset-provisioning`),
  repairSequences: () => api.post('/superadmin/system/repair-sequences'),
  syncRoles: () => api.post('/superadmin/sync-roles'),
};

// Subscription API
export const subscriptionAPI = {
  get: (endpoint) => api.get(`/subscriptions${endpoint}`),
  post: (endpoint, data) => api.post(`/subscriptions${endpoint}`, data),
  put: (endpoint, data) => api.put(`/subscriptions${endpoint}`, data),

  // Upgrade & Cycle
  upgradeIntent: (data) => api.post('/subscriptions/upgrade-intent', data),
  upgrade: (data) => api.post('/subscriptions/upgrade', data),
  changeCycle: (data) => api.put('/subscriptions/cycle', data),

  // Addons â€” use /subscriptions/ (plural) to match the backend router mount point
  getAddons: (subId) => api.get(`/subscriptions/${subId}/addons`),
  createAddonIntent: (subId, data) => api.post(`/subscriptions/${subId}/addons/intent`, data),
  purchaseAddon: (subId, data) => api.post(`/subscriptions/${subId}/addons`, data),
  cancelAddon: (subId, addonId) => api.delete(`/subscriptions/${subId}/addons/${addonId}`),
};

// Currencies API
export const currenciesAPI = {
  getAll: (params) => api.get('/currencies', { params }),
  getById: (id) => api.get(`/currencies/${id}`),
  create: (currencyData) => api.post('/currencies', currencyData),
  update: (id, currencyData) => api.put(`/currencies/${id}`, currencyData),
  delete: (id) => api.delete(`/currencies/${id}`),
  setBase: (id) => api.put(`/currencies/${id}/set-base`),
  getExchangeRate: (fromId, toId) => api.get(`/currencies/exchange-rate/${fromId}/${toId}`),
};

// Multi-Currency & FX Subsystem API
export const fxAPI = {
  getRate: (params) => api.get('/fx/rate', { params }),
  getRates: (params) => api.get('/fx/rates', { params }),
  createRate: (data) => api.post('/fx/rates', data),
  convertPreview: (data) => api.post('/fx/convert-preview', data),
  settlePreview: (data) => api.post('/fx/settle-preview', data),
  getPolicy: () => api.get('/fx/policy'),
  updatePolicy: (data) => api.put('/fx/policy', data),
  getRevaluations: (params) => api.get('/fx/revaluations', { params }),
  previewRevaluation: (data) => api.post('/fx/revaluations/preview', data),
  executeRevaluation: (data) => api.post('/fx/revaluations/execute', data),
  reverseRevaluation: (id, data) => api.post(`/fx/revaluations/${id}/reverse`, data)
};



// Departments API
export const departmentsAPI = {
  getAll: (params) => api.get('/departments', { params }),
  getById: (id) => api.get(`/departments/${id}`),
  create: (departmentData) => api.post('/departments', departmentData),
  update: (id, departmentData) => api.put(`/departments/${id}`, departmentData),
  delete: (id) => api.delete(`/departments/${id}`),
  peek: () => api.get('/numbering/peek/department'),
};

// Projects API
export const projectsAPI = {
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  create: (projectData) => api.post('/projects', projectData),
  update: (id, projectData) => api.put(`/projects/${id}`, projectData),
  delete: (id) => api.delete(`/projects/${id}`)
};


// Enterprise Tasks API
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  // Activities
  getActivities: (taskId) => api.get(`/tasks/${taskId}/activities`),
  createActivity: (taskId, data) => api.post(`/tasks/${taskId}/activities`, data),
  updateActivity: (taskId, activityId, data) => api.put(`/tasks/${taskId}/activities/${activityId}`, data),
  deleteActivity: (taskId, activityId) => api.delete(`/tasks/${taskId}/activities/${activityId}`),
  // Progress
  getProgressLog: (taskId) => api.get(`/tasks/${taskId}/progress-log`),
  verifyProgress: (taskId, data) => api.put(`/tasks/${taskId}/verify-progress`, data),
};

// Employee Targets API
export const employeeTargetsAPI = {
  getAll: (params) => api.get('/employee-targets', { params }),
  getById: (id) => api.get(`/employee-targets/${id}`),
  create: (data) => api.post('/employee-targets', data),
  update: (id, data) => api.put(`/employee-targets/${id}`, data),
  delete: (id) => api.delete(`/employee-targets/${id}`),
  refresh: (id) => api.post(`/employee-targets/${id}/refresh`),
};

// KPI Definitions API
export const kpiAPI = {
  getAll: (params) => api.get('/kpis', { params }),
  getById: (id) => api.get(`/kpis/${id}`),
  create: (data) => api.post('/kpis', data),
  update: (id, data) => api.put(`/kpis/${id}`, data),
  delete: (id) => api.delete(`/kpis/${id}`),
};

// Performance Dashboard API
export const performanceAPI = {
  // Periods
  getPeriods: (params) => api.get('/performance/periods', { params }),
  createPeriod: (data) => api.post('/performance/periods', data),
  updatePeriod: (id, data) => api.put(`/performance/periods/${id}`, data),
  finalizePeriod: (id) => api.post(`/performance/periods/${id}/finalize`),
  // Snapshots
  getSnapshots: (params) => api.get('/performance/snapshots', { params }),
  computeSnapshot: (data) => api.post('/performance/snapshots/compute', data),
  // Dashboard
  getDashboard: (params) => api.get('/performance/dashboard', { params }),
  getEmployeeTimeline: (employeeId) => api.get(`/performance/dashboard/employee/${employeeId}/timeline`),
  getWorkload: (params) => api.get('/performance/dashboard/workload', { params }),
};

// Project Timesheets API
export const projectTimesheetsAPI = {
  getAll: (params) => api.get('/project-timesheets', { params }),
  getById: (id) => api.get(`/project-timesheets/${id}`),
  create: (data) => api.post('/project-timesheets', data),
  update: (id, data) => api.put(`/project-timesheets/${id}`, data),
  delete: (id) => api.delete(`/project-timesheets/${id}`),
  submit: (id) => api.post(`/project-timesheets/${id}/submit`),
  approve: (id, data) => api.post(`/project-timesheets/${id}/approve`, data),
};

// Employees API
export const employeesAPI = {
  list: (params) => api.get('/payroll/employees/list', { params }),
};

// Work Shifts API
export const workShiftsAPI = {
  getAll: (params) => api.get('/hr/shifts', { params }),
  create: (data) => api.post('/hr/shifts', data),
  update: (id, data) => api.put(`/hr/shifts/${id}`, data),
  delete: (id) => api.delete(`/hr/shifts/${id}`),
};

// Rota API
export const rotaAPI = {
  getAll: (params) => api.get('/hr/rota', { params }),
  upsert: (data) => api.post('/hr/rota', data),
  bulkAssign: (data) => api.post('/hr/rota/bulk', data),
  publish: (data) => api.post('/hr/rota/publish', data),
  delete: (id) => api.delete(`/hr/rota/${id}`),
};
// Workflow Settings API
export const workflowSettingsAPI = {
  getDefinitions: (params) => api.get('/workflows/definitions', { params }),
  saveOverride: (data) => api.post('/workflows/overrides', data),
  deleteOverride: (entityType) => api.delete(`/workflows/overrides/${entityType}`),
  getPrebuiltTags: () => api.get('/workflow-tags'),
  saveTagOverride: (data) => api.post('/workflow-tags', data),
};

// API Settings API
export const apiSettingsAPI = {
  getCredentials: () => api.get('/api-credentials'),
  generate: () => api.post('/api-credentials/generate'),
  regenerate: (confirmed = true) => api.post('/api-credentials/regenerate', { confirmed }),
};

// Website API
export const websiteAPI = {
  submitDemoRequest: (data) => api.post('/website/demo', data),
  submitContactInquiry: (data) => api.post('/website/contact', data),
};

// Loyalty API
export const loyaltyAPI = {
  getPrograms: (params) => api.get('/loyalty/programs', { params }),
  createProgram: (data, config) => api.post('/loyalty/programs', data, config),
  updateProgram: (id, data) => api.put(`/loyalty/programs/${id}`, data),
  deleteProgram: (id) => api.delete(`/loyalty/programs/${id}`),
  getCustomers: (params) => api.get('/loyalty/customers', { params }),
  getCustomerDetails: (customerId) => api.get(`/loyalty/customers/${customerId}`),
  adjustPoints: (customerId, data, config) => api.post(`/loyalty/customers/${customerId}/adjust-points`, data, config),
};

// Expense Policy API
export const expensePolicyAPI = {
  get: () => api.get('/expenses/policy'),
  update: (data) => api.put('/expenses/policy', data),
};

// System Alerts API
export const systemAlertsAPI = {
  getAll: (params) => api.get('/system-alerts', { params }),
  getById: (id) => api.get(`/system-alerts/${id}`),
  resolve: (id, resolution) => api.post(`/system-alerts/${id}/resolve`, { resolution }),
  resolveAll: (ids, resolution) => api.post('/system-alerts/resolve-all', { ids, resolution }),
  acknowledge: (id) => api.post(`/system-alerts/${id}/acknowledge`),
  getSummary: () => api.get('/system-alerts/summary'),
};

// Meta API
export const metaAPI = {
  getEnums: () => api.get('/meta/enums'),
};

// Fiscal API
export const fiscalAPI = {
  checkLock: (date) => api.get('/fiscal/check-lock', { params: { date } }),
};

// Revenue Policy API
export const revenuePolicyAPI = {
  get: () => api.get('/revenue-policy'),
  update: (data) => api.put('/revenue-policy', data),
};

// Document Numbering API
export const numberingAPI = {
  peek: (type, params) => api.get(`/numbering/peek/${type}`, { params }),
};

// AI Chat API
export const aiAPI = {
  chat: (message, config = {}) => api.post('/ai/chat', { message }, config),
  commitDraft: (draftId) => api.post(`/ai/drafts/${draftId}/commit`),
  publicChat: (message) => api.post('/ai/public-chat', { message }),
  getHistory: () => api.get('/ai/history'),
  saveForecastScenario: (data) => api.post('/ai/forecasts/scenarios', data),
  submitForecastForApproval: (id) => api.post(`/ai/forecasts/${id}/approve`),
  getForecastAccuracy: () => api.get('/ai/forecasts/accuracy'),
  getVersions: () => api.get('/ai/forecasts'),
};

export const cycleCountAPI = {
  getAll: (params) => api.get('/cycle-counts', { params }),
  start: (data) => api.post('/cycle-counts/start', data),
  complete: (id, data) => api.post(`/cycle-counts/${id}/complete`, data),
  saveDraft: (data) => api.post('/cycle-counts/draft', data),
  getDraft: () => api.get('/cycle-counts/draft'),
  clearDraft: () => api.delete('/cycle-counts/draft'),
};

export const landedCostAPI = {
  getAll: (params) => api.get('/landed-costs', { params }),
  create: (data) => api.post('/landed-costs', data),
  allocate: (id, data) => api.post(`/landed-costs/${id}/allocate`, data),
  confirm: (id) => api.post(`/landed-costs/${id}/confirm`),
  void: (id) => api.post(`/landed-costs/${id}/void`),
};

// Budgets API
export const budgetsAPI = {
  getAll: (params) => api.get('/budgets', { params }),
  getById: (id) => api.get(`/budgets/${id}`),
  create: (data) => api.post('/budgets', data),
  update: (id, data) => api.put(`/budgets/${id}`, data),
  delete: (id) => api.delete(`/budgets/${id}`),
  getVariance: (id) => api.get(`/budgets/${id}/variance`),
  getRevisions: (id) => api.get(`/budgets/${id}/revisions`),
  getAuditTrail: (id) => api.get(`/budgets/${id}/audit`),
  lock: (id) => api.post(`/budgets/${id}/lock`),
  unlock: (id) => api.post(`/budgets/${id}/unlock`),
  submitForApproval: (id) => api.post(`/budgets/${id}/submit`),
  approve: (id) => api.post(`/budgets/${id}/approve`),
  cancel: (id) => api.post(`/budgets/${id}/cancel`),
  close: (id) => api.post(`/budgets/${id}/close`),
  archive: (id) => api.post(`/budgets/${id}/archive`),
  createRevision: (id) => api.post(`/budgets/${id}/revisions`),
  reject: (id, reason, reasonCode) => api.post(`/budgets/${id}/reject`, {
    reason,
    reasonCode,
    reasonComment: reason  // For backward compatibility
  }),

  // Allocations
  getAllocations: (params) => api.get('/budgets/allocations', { params }),
  createAllocation: (data) => api.post('/budgets/allocations', data),
  submitAllocation: (id) => api.post(`/budgets/allocations/${id}/submit`),
  approveAllocation: (id) => api.post(`/budgets/allocations/${id}/approve`),
  rejectAllocation: (id, reason) => api.post(`/budgets/allocations/${id}/reject`, { reason }),

  // Periods
  getPeriods: (params) => api.get('/budgets/periods', { params }),
  createPeriod: (data) => api.post('/budgets/periods', data),
  updatePeriod: (id, data) => api.put(`/budgets/periods/${id}`, data),
  deletePeriod: (id) => api.delete(`/budgets/periods/${id}`),

  // Roll Forward
  rollForward: (id, data) => api.post(`/budgets/${id}/roll-forward`, data),
};

// Ledgers API
export const ledgersAPI = {
  getAll: () => api.get('/ledgers'),
  create: (data) => api.post('/ledgers', data),
  update: (id, data) => api.patch(`/ledgers/${id}`, data),
};

// Accounting Governance API
export const governanceAPI = {
  replayEvent: (eventId, data) => api.post(`/accounting-governance/replay/${eventId}`, data),
  verifyEvent: (eventId) => api.get(`/accounting-governance/verify/${eventId}`),
  getInvariants: () => api.get('/accounting-governance/invariants'),
  analyzeShadowDrift: (eventId) => api.post(`/accounting-governance/shadow-analyze/${eventId}`),
  getOutbox: (params) => api.get('/accounting-governance/outbox', { params }),
  retryOutbox: (id) => api.post(`/accounting-governance/outbox/retry/${id}`),
};

// UK Compliance & HMRC MTD API
export const ukComplianceAPI = {
  getVATReturn: (params) => api.get('/uk-compliance/vat-return', { params }),
  submitVATReturn: (data) => api.post('/uk-compliance/vat-submit', data),
  getAuditBundle: (vatReturnId) => api.get(`/uk-compliance/audit-bundle/${vatReturnId}`, { responseType: 'blob' }),
};

// Tax Governance API
export const taxGovernanceAPI = {
  getObligations: () => api.get('/tax-governance/obligations'),
  checkDrift: (jurisdiction) => api.post('/tax-governance/check-drift', { jurisdiction }),
  getAudit: (transactionId) => api.get(`/tax-governance/audit/${transactionId}`),
  reProcess: (obligationId) => api.post(`/tax-governance/re-process/${obligationId}`),
};

// Sales Returns API
export const salesReturnsAPI = {
  getAll: (params) => api.get('/sales-returns', { params }),
  getById: (id) => api.get(`/sales-returns/${id}`),
  authorize: (data) => api.post('/sales-returns/authorize', data),
  update: (id, data) => api.put(`/sales-returns/${id}`, data),
  submit: (id) => api.post(`/sales-returns/${id}/submit`),
  approve: (id) => api.post(`/sales-returns/${id}/approve`),
  reject: (id, data) => api.post(`/sales-returns/${id}/reject`, data),
  receive: (id, data) => api.post(`/sales-returns/${id}/receive`, data),
  inspect: (id, data) => api.post(`/sales-returns/${id}/inspect`, data),
  financeReview: (id) => api.post(`/sales-returns/${id}/finance-review`),
  generateCreditNote: (id) => api.post(`/sales-returns/${id}/generate-credit-note`),
  delete: (id) => api.delete(`/sales-returns/${id}`),
};

// Purchase Returns API
export const purchaseReturnsAPI = {
  create: (data) => api.post('/purchase-returns', data),
  saveDraft: (data) => api.post('/purchase-returns', { ...data, status: 'draft' }),
  getAll: (params) => api.get('/purchase-returns', { params }),
  getById: (id) => api.get(`/purchase-returns/${id}`),
  update: (id, data) => api.put(`/purchase-returns/${id}`, data),
  authorize: (data) => api.post('/purchase-returns/authorize', data),
  submit: (id) => api.post(`/purchase-returns/${id}/submit`),
  approve: (id) => api.post(`/purchase-returns/${id}/approve`),
  reject: (id, data) => api.post(`/purchase-returns/${id}/reject`, data),
  ship: (id, data) => api.post(`/purchase-returns/${id}/ship`, data),
  financeReview: (id) => api.post(`/purchase-returns/${id}/finance-review`),
  generateDebitNote: (id) => api.post(`/purchase-returns/${id}/generate-debit-note`),
  delete: (id) => api.delete(`/purchase-returns/${id}`),
};

// Goods Receipt Notes API
export const goodsReceiptNotesAPI = {
  getAll: (params) => api.get('/goods-receipt-notes', { params }),
  getReturnable: (params) => api.get('/goods-receipt-notes/returnable', { params }),
  getById: (id, params) => api.get(`/goods-receipt-notes/${id}`, { params }),
  create: (data) => api.post('/goods-receipt-notes', data),
  update: (id, data) => api.put(`/goods-receipt-notes/${id}`, data),
  submit: (id) => api.post(`/goods-receipt-notes/${id}/submit`),
  approve: (id) => api.post(`/goods-receipt-notes/${id}/approve`),
};

// POS Backorder API
export const posBackorderAPI = {
  getPending: () => api.get('/pos/backorders/pending'),
  approve: (outboxId) => api.post(`/pos/backorders/${outboxId}/approve`),
  reject: (outboxId) => api.post(`/pos/backorders/${outboxId}/reject`),
  convertToPO: (outboxId, payload) => api.post(`/pos/backorders/${outboxId}/convert-to-po`, payload),
};

// Fixed Assets API
export const fixedAssetsAPI = {
  getAll: (params) => api.get('/fixed-assets', { params }),
  getById: (id) => api.get(`/fixed-assets/${id}`),
  getCategories: () => api.get('/fixed-assets/categories'),
  createCategory: (data) => api.post('/fixed-assets/categories', data),
  updateCategory: (id, data) => api.put(`/fixed-assets/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/fixed-assets/categories/${id}`),
  create: (data) => api.post('/fixed-assets', data),
  update: (id, data) => api.put(`/fixed-assets/${id}`, data),
  delete: (id) => api.delete(`/fixed-assets/${id}`),
  depreciate: (id, data) => api.post(`/fixed-assets/${id}/depreciate`, data),
  dispose: (id, data) => api.post(`/fixed-assets/${id}/dispose`, data),
  // Attachments
  getAttachments: (id) => api.get(`/fixed-assets/${id}/attachments`),
  uploadImages: (id, formData) => api.post(`/fixed-assets/${id}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  deleteImage: (id, attachmentId) => api.delete(`/fixed-assets/${id}/images/${attachmentId}`),
  reconcileCapitalizations: () => api.post('/fixed-assets/reconcile-capitalizations'),
};

// Manufacturing API
export const manufacturingAPI = {
  // BOMs
  getBOMs: (params) => api.get('/manufacturing/boms', { params }),
  getBOMById: (id) => api.get(`/manufacturing/boms/${id}`),
  createBOM: (data) => api.post('/manufacturing/boms', data),
  updateBOM: (id, data) => api.put(`/manufacturing/boms/${id}`, data),
  deleteBOM: (id) => api.delete(`/manufacturing/boms/${id}`),
  activateBOMVersion: (id) => api.post(`/manufacturing/boms/versions/${id}/activate`),

  // Production Orders
  getProductionOrders: (params) => api.get('/manufacturing/production-orders', { params }),
  getProductionOrderById: (id) => api.get(`/manufacturing/production-orders/${id}`),
  createProductionOrder: (data) => api.post('/manufacturing/production-orders', data),
  updateProductionOrder: (id, data) => api.put(`/manufacturing/production-orders/${id}`, data),
  releaseProductionOrder: (id) => api.post(`/manufacturing/production-orders/${id}/release`),
  cancelProductionOrder: (id, data) => api.post(`/manufacturing/production-orders/${id}/cancel`, data),
  issueMaterials: (id, data) => api.post(`/manufacturing/production-orders/${id}/issue`, data),
  completeProduction: (id, data) => api.post(`/manufacturing/production-orders/${id}/complete`, data),

  // Work Centers
  getWorkCenters: (params) => api.get('/manufacturing/work-centers', { params }),
  getWorkCenterById: (id) => api.get(`/manufacturing/work-centers/${id}`),
  createWorkCenter: (data) => api.post('/manufacturing/work-centers', data),
  updateWorkCenter: (id, data) => api.put(`/manufacturing/work-centers/${id}`, data),
  deleteWorkCenter: (id) => api.delete(`/manufacturing/work-centers/${id}`),

  // Routings
  getRoutings: (params) => api.get('/manufacturing/routings', { params }),
  getRoutingById: (id) => api.get(`/manufacturing/routings/${id}`),
  createRouting: (data) => api.post('/manufacturing/routings', data),
  updateRouting: (id, data) => api.put(`/manufacturing/routings/${id}`, data),
  deleteRouting: (id) => api.delete(`/manufacturing/routings/${id}`),
};

// Shareholders API
export const shareholdersAPI = {
  getAll: (params) => api.get('/shareholders', { params }),
  getById: (id) => api.get(`/shareholders/${id}`),
  create: (data) => api.post('/shareholders', data),
  update: (id, data) => api.put(`/shareholders/${id}`, data),
  createTransaction: (data) => api.post('/shareholders/transactions', data),
};

// Brands API
export const brandsAPI = {
  getAll: (params) => api.get('/brands', { params }),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`),
  uploadLogo: (formData) => api.post('/brands/upload-logo', formData, { timeout: 60000 }),
  peek: () => api.get('/numbering/peek/brand'),
};

// Warehouse Bins API
export const warehouseBinsAPI = {
  getAll: (params) => api.get('/warehouse-bins', { params }),
  create: (data) => api.post('/warehouse-bins', data),
  update: (id, data) => api.put(`/warehouse-bins/${id}`, data),
  delete: (id) => api.delete(`/warehouse-bins/${id}`),
};

// Approvals API
export const approvalsAPI = {
  getPending: () => api.get('/approvals/pending'),
  approveInvoice: (id, data) => api.post(`/approvals/invoices/${id}/approve`, data),
  approveExpense: (id, data) => api.post(`/approvals/expenses/${id}/approve`, data),
  rejectExpense: (id, data) => api.post(`/approvals/expenses/${id}/reject`, data),

  getDefinitions: () => api.get('/approvals/definitions'),
  createDefinition: (data) => api.post('/approvals/definitions', data),
  updateDefinition: (id, data) => api.put(`/approvals/definitions/${id}`, data),
  deleteDefinition: (id) => api.delete(`/approvals/definitions/${id}`),

  getAll: (params) => api.get('/approvals', { params }),
  getById: (id) => api.get(`/approvals/${id}`),
  approve: (id, data) => api.post(`/approvals/${id}/approve`, data),
  reject: (id, data) => api.post(`/approvals/${id}/reject`, data),
};

// Blog / Content API
export const blogAPI = {
  // Public
  getAll: (params) => api.get('/blogs', { params }),
  getBySlug: (slug) => api.get(`/blogs/${slug}`),
  getCategories: () => api.get('/blogs/categories'),
  getFeatured: () => api.get('/blogs/featured'),
  getRelated: (id) => api.get(`/blogs/related/${id}`),
  search: (params) => api.get('/blogs/search', { params }),
  // Admin
  adminList: (params) => api.get('/blogs/admin/list', { params }),
  adminCreate: (data) => api.post('/blogs/admin', data),
  adminUpdate: (id, data) => api.put(`/blogs/admin/${id}`, data),
  adminDelete: (id) => api.delete(`/blogs/admin/${id}`),
  adminDuplicate: (id) => api.post(`/blogs/admin/${id}/duplicate`),
  adminUploadImage: (formData) => api.post('/blogs/admin/upload-image', formData, { timeout: 120000 }),
  // Admin Categories
  adminCatList: () => api.get('/blogs/admin/categories/list'),
  adminCatCreate: (data) => api.post('/blogs/admin/categories', data),
  adminCatUpdate: (id, data) => api.put(`/blogs/admin/categories/${id}`, data),
  adminCatDelete: (id) => api.delete(`/blogs/admin/categories/${id}`),
};

// AI Documents API
export const aiDocumentsAPI = {
  upload: (data, config) => api.post('/ai-documents/upload', data, config),
  getAll: (params) => api.get('/ai-documents', { params }),
  getById: (id) => api.get(`/ai-documents/${id}`),
  approve: (id, payload) => api.post(`/ai-documents/${id}/approve`, payload),
  bulkApprove: (payload) => api.post('/ai-documents/bulk-approve', payload),
  reject: (id, payload) => api.post(`/ai-documents/${id}/reject`, payload),
  delete: (id) => api.delete(`/ai-documents/${id}`),
  getDLQ: () => api.get('/ai-documents/dlq'),
  requeueDLQ: (index) => api.post(`/ai-documents/dlq/${index}/requeue`),
  getFileUrl: (id) => api.get(`/ai-documents/${id}/file`),
};

// Trade & International Commerce API
export const tradeShipmentsAPI = {
  getAll: (params) => api.get('/trade/shipments', { params }),
  getSummary: (params) => api.get('/trade/shipments/summary', { params }),
  getById: (id) => api.get(`/trade/shipments/${id}`),
  create: (data) => api.post('/trade/shipments', data),
  update: (id, data) => api.put(`/trade/shipments/${id}`, data),
  delete: (id) => api.delete(`/trade/shipments/${id}`),
  confirm: (id) => api.post(`/trade/shipments/${id}/confirm`),
  customsClear: (id, data) => api.post(`/trade/shipments/${id}/customs-clear`, data),
  peek: () => api.get('/numbering/peek/trade_shipment'),
};

export const lettersOfCreditAPI = {
  getAll: (params) => api.get('/trade/letters-of-credit', { params }),
  getById: (id) => api.get(`/trade/letters-of-credit/${id}`),
  create: (data) => api.post('/trade/letters-of-credit', data),
  update: (id, data) => api.put(`/trade/letters-of-credit/${id}`, data),
  submit: (id) => api.post(`/trade/letters-of-credit/${id}/issue`),
  issue: (id) => api.post(`/trade/letters-of-credit/${id}/issue`),
  amend: (id, data) => api.post(`/trade/letters-of-credit/${id}/amend`, data),
  utilize: (id, amount) => api.post(`/trade/letters-of-credit/${id}/utilize`, { amount }),
  close: (id) => api.post(`/trade/letters-of-credit/${id}/close`),
  peek: () => api.get('/numbering/peek/letter_of_credit'),
};

export const bankGuaranteesAPI = {
  getAll: (params) => api.get('/trade/bank-guarantees', { params }),
  getById: (id) => api.get(`/trade/bank-guarantees/${id}`),
  create: (data) => api.post('/trade/bank-guarantees', data),
  update: (id, data) => api.put(`/trade/bank-guarantees/${id}`, data),
  issue: (id) => api.post(`/trade/bank-guarantees/${id}/issue`),
  claim: (id, data) => api.post(`/trade/bank-guarantees/${id}/claim`, data),
  peek: () => api.get('/numbering/peek/bank_guarantee'),
};

export const customsDeclarationsAPI = {
  getAll: (params) => api.get('/trade/customs-declarations', { params }),
  getById: (id) => api.get(`/trade/customs-declarations/${id}`),
  create: (data) => api.post('/trade/customs-declarations', data),
  update: (id, data) => api.put(`/trade/customs-declarations/${id}`, data),
  calculateDuty: (id) => api.post(`/trade/customs-declarations/${id}/calculate-duty`),
  submit: (id) => api.post(`/trade/customs-declarations/${id}/submit`),
  approve: (id) => api.post(`/trade/customs-declarations/${id}/approve`),
  reject: (id, reason) => api.post(`/trade/customs-declarations/${id}/reject`, { reason }),
  peek: () => api.get('/numbering/peek/customs_declaration'),
};

export const hsCodesAPI = {
  getAll: (params) => api.get('/trade/hs-codes', { params }),
  getById: (id) => api.get(`/trade/hs-codes/${id}`),
  create: (data) => api.post('/trade/hs-codes', data),
  update: (id, data) => api.put(`/trade/hs-codes/${id}`, data),
  delete: (id) => api.delete(`/trade/hs-codes/${id}`),
};

export const countryOfOriginAPI = {
  getAll: (params) => api.get('/trade/country-of-origin', { params }),
  getById: (id) => api.get(`/trade/country-of-origin/${id}`),
  create: (data) => api.post('/trade/country-of-origin', data),
  update: (id, data) => api.put(`/trade/country-of-origin/${id}`, data),
  delete: (id) => api.delete(`/trade/country-of-origin/${id}`),
};

export const tradeComplianceAPI = {
  screen: (data) => api.post('/trade/compliance/screen', data),
  getResults: (params) => api.get('/trade/compliance/results', { params }),
  override: (id, reason) => api.post(`/trade/compliance/${id}/override`, { reason }),
};

// Vendor Quotations API
export const vendorQuotationsAPI = {
  getAll: (params) => api.get('/vendor-quotations', { params }),
  getById: (id) => api.get(`/vendor-quotations/${id}`),
  create: (data) => api.post('/vendor-quotations', data),
  update: (id, data) => api.put(`/vendor-quotations/${id}`, data),
  delete: (id) => api.delete(`/vendor-quotations/${id}`),
  getComparativeStatement: (prId, params) => api.get(`/vendor-quotations/comparative/${prId}`, { params }),
  awardLines: (data) => api.post('/vendor-quotations/award', data),
  generatePOs: (prId) => api.post(`/vendor-quotations/generate-pos/${prId}`),
  peek: () => api.get('/numbering/peek/vendor_quote'),
};

// Customer Pricing API
export const customerPricingAPI = {
  getAll: (params) => api.get('/customer-pricing', { params }),
  create: (data) => api.post('/customer-pricing', data),
  update: (id, data) => api.put(`/customer-pricing/${id}`, data),
  delete: (id) => api.delete(`/customer-pricing/${id}`),
  resolve: (data) => api.post('/customer-pricing/resolve', data),
};

// Customer Groups API
export const customerGroupsAPI = {
  getAll: (params) => api.get('/customer-groups', { params }),
  getById: (id) => api.get(`/customer-groups/${id}`),
  create: (data) => api.post('/customer-groups', data),
  update: (id, data) => api.put(`/customer-groups/${id}`, data),
  delete: (id) => api.delete(`/customer-groups/${id}`),
  assign: (id, data) => api.post(`/customer-groups/${id}/assign`, data),
  remove: (id, data) => api.post(`/customer-groups/${id}/remove`, data),
};

export const vendorGroupsAPI = {
  getAll: (params) => api.get('/vendor-groups', { params }),
  getById: (id) => api.get(`/vendor-groups/${id}`),
  create: (data) => api.post('/vendor-groups', data),
  update: (id, data) => api.put(`/vendor-groups/${id}`, data),
  delete: (id) => api.delete(`/vendor-groups/${id}`),
  toggleStatus: (id, isActive) => api.patch(`/vendor-groups/${id}/status`, { isActive }),
  assign: (id, data) => api.post(`/vendor-groups/${id}/assign`, data),
  remove: (id, data) => api.post(`/vendor-groups/${id}/remove`, data),
};

// QC Inspections API

// QC Inspections API
export const qcAPI = {
  getSpecifications: (params) => api.get('/qc-inspections/specifications', { params }),
  createSpecification: (data) => api.post('/qc-inspections/specifications', data),
  updateSpecification: (id, data) => api.put(`/qc-inspections/specifications/${id}`, data),
  deleteSpecification: (id) => api.delete(`/qc-inspections/specifications/${id}`),
  getAll: (params) => api.get('/qc-inspections', { params }),
  getById: (id) => api.get(`/qc-inspections/${id}`),
  create: (data) => api.post('/qc-inspections', data),
  update: (id, data) => api.put(`/qc-inspections/${id}`, data),
  submit: (id) => api.post(`/qc-inspections/${id}/submit`),
  peek: () => api.get('/numbering/peek/qc_inspection'),
};

// Packing Orders API
export const packingOrdersAPI = {
  getAll: (params) => api.get('/packing-orders', { params }),
  getById: (id) => api.get(`/packing-orders/${id}`),
  create: (data) => api.post('/packing-orders', data),
  update: (id, data) => api.put(`/packing-orders/${id}`, data),
  delete: (id) => api.delete(`/packing-orders/${id}`),
  confirm: (id) => api.post(`/packing-orders/${id}/confirm`),
  peek: () => api.get('/numbering/peek/packing_order'),
};

// Petty Cash API
export const pettyCashAPI = {
  getFunds: (params) => api.get('/petty-cash/funds', { params }),
  createFund: (data) => api.post('/petty-cash/funds', data),
  updateFund: (id, data) => api.put(`/petty-cash/funds/${id}`, data),
  getFundBalance: (id) => api.get(`/petty-cash/funds/${id}/balance`),
  getTransactions: (fundId, params) => api.get(`/petty-cash/funds/${fundId}/transactions`, { params }),
  createTransaction: (data) => api.post('/petty-cash/transactions', data),
  submitTransaction: (id) => api.post(`/petty-cash/transactions/${id}/submit`),
  postTransaction: (id) => api.post(`/petty-cash/transactions/${id}/post`),
  peek: () => api.get('/numbering/peek/petty_cash'),
};

// Biometric API
export const biometricAPI = {
  getDevices: (params) => api.get('/biometric/devices', { params }),
  createDevice: (data) => api.post('/biometric/devices', data),
  updateDevice: (id, data) => api.put(`/biometric/devices/${id}`, data),
  deleteDevice: (id) => api.delete(`/biometric/devices/${id}`),
  syncDevice: (id) => api.post(`/biometric/devices/${id}/sync`),
  getPunchLogs: (params) => api.get('/biometric/punch-logs', { params }),
};

// Access Restrictions API
export const accessRestrictionsAPI = {
  // Scopes (companies + roles for dropdowns)
  getScopes: () => api.get('/security/access-restrictions/scopes'),
  // Policies
  listPolicies: (params) => api.get('/security/access-restrictions/policies', { params }),
  getEffective: (params) => api.get('/security/access-restrictions/policies/effective', { params }),
  createPolicy: (data) => api.post('/security/access-restrictions/policies', data),
  updatePolicy: (id, data) => api.patch(`/security/access-restrictions/policies/${id}`, data),
  deletePolicy: (id) => api.delete(`/security/access-restrictions/policies/${id}`),
  // Devices
  listDevices: (params) => api.get('/security/access-restrictions/devices', { params }),
  registerDevice: (data) => api.post('/security/access-restrictions/devices', data),
  updateDevice: (id, data) => api.patch(`/security/access-restrictions/devices/${id}`, data),
  deleteDevice: (id) => api.delete(`/security/access-restrictions/devices/${id}`),
  verifyDevice: (deviceToken) => api.post('/security/access-restrictions/devices/verify', { deviceToken }),
};

// Security API
export const securityAPI = {
  getPolicy: () => api.get('/security/policy'),
  updatePolicy: (section, data, reason) => api.patch('/security/policy', { section, data, reason }),
  getPolicyVersions: () => api.get('/security/policy/versions'),
  restoreVersion: (id, reason) => api.post(`/security/policy/versions/${id}/restore`, { reason }),
  getDashboard: () => api.get('/security/dashboard'),
  getAlerts: (params) => api.get('/security/alerts', { params }),
  acknowledgeAlert: (id) => api.patch(`/security/alerts/${id}/acknowledge`),
  getSessions: () => api.get('/auth/sessions'),
  revokeSession: (id) => api.delete(`/auth/sessions/${id}`),
  revokeAllSessions: () => api.delete('/auth/sessions'),
  trustSession: (id, days) => api.post(`/auth/sessions/${id}/trust`, { days }),
  forcePasswordReset: (userId) => api.post(`/users/${userId}/force-password-reset`),
  unlockAccount: (userId) => api.post(`/users/${userId}/unlock`),
  disableAccount: (userId, reason) => api.patch(`/users/${userId}/disable`, { reason }),
};

// EAM API
export const fetchAssetAcquisitionRequests = (params) => api.get('/asset-acquisition-requests', { params });
export const getAssetAcquisitionRequest = (id) => api.get(`/asset-acquisition-requests/${id}`);
export const createAssetAcquisitionRequest = (data) => api.post('/asset-acquisition-requests', data);
export const updateAssetAcquisitionRequest = (id, data) => api.put(`/asset-acquisition-requests/${id}`, data);
export const deleteAssetAcquisitionRequest = (id, reason) => api.delete(`/asset-acquisition-requests/${id}`, { data: { reason } });
export const submitAssetAcquisitionRequest = (id) => api.post(`/asset-acquisition-requests/${id}/submit`);
export const approveAssetAcquisitionRequest = (id) => api.post(`/asset-acquisition-requests/${id}/approve`);
export const rejectAssetAcquisitionRequest = (id, reason) => api.post(`/asset-acquisition-requests/${id}/reject`, { reason });
export const previewAssetConversion = (data) => api.post('/asset-acquisition-requests/preview-conversion', data);
export const convertAssetToPR = (data) => api.post('/asset-acquisition-requests/convert-to-pr', data);
export const fetchAssetTraceability = (id) => api.get(`/asset-acquisition-requests/${id}/traceability`);
export const uploadAssetLineAttachments = (lineId, formData) => api.post(`/asset-acquisition-requests/lines/${lineId}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const fetchAssetLineAttachments = (lineId) => api.get(`/asset-acquisition-requests/lines/${lineId}/attachments`);
export const deleteAssetLineAttachment = (lineId, attachmentId) => api.delete(`/asset-acquisition-requests/lines/${lineId}/attachments/${attachmentId}`);
export const fetchAssetCapitalizations = (params) => api.get('/asset-capitalizations', { params });
export const resolveCapitalizationSourceValues = (data) => api.post('/asset-capitalizations/resolve-source-values', data);
export const createAssetCapitalization = (data) => api.post('/asset-capitalizations', data);
export const updateAssetCapitalization = (id, data) => api.put(`/asset-capitalizations/${id}`, data);
export const deleteAssetCapitalization = (id) => api.delete(`/asset-capitalizations/${id}`);
export const postAssetCapitalization = (id) => api.post(`/asset-capitalizations/${id}/post`);
export const updateAssetCapitalizationStatus = (id, status) => api.patch(`/asset-capitalizations/${id}/status`, { status });
export const fetchAssetDisposals = (params) => api.get('/asset-disposals', { params });
export const createAssetDisposal = (data) => api.post('/asset-disposals', data);
export const fetchAssetTransfers = (params) => api.get('/asset-transfers', { params });
export const createAssetTransfer = (data) => api.post('/asset-transfers', data);
export const fetchAssetCategories = (params) => api.get('/fixed-assets/categories', { params });

// Leases API (IFRS 16 / ASC 842 / FRS 102)
export const leasesAPI = {
  getNextNumber: () => api.get('/leases/next-number'),
  getSummary: () => api.get('/leases/summary'),
  getDisclosures: (financialYear) => api.get('/leases/disclosures', { params: { financialYear } }),
  getLeases: (params) => api.get('/leases', { params }),
  getLease: (id) => api.get(`/leases/${id}`),
  calculatePreview: (data) => api.post('/leases/calculate-preview', data),
  createLease: (data) => api.post('/leases', data),
  updateLease: (id, data) => api.put(`/leases/${id}`, data),
  commenceLease: (id) => api.post(`/leases/${id}/commence`),
  postPeriod: (id, periodNumber) => api.post(`/leases/${id}/post-period/${periodNumber}`),
  modifyLease: (id, data) => api.post(`/leases/${id}/modify`, data),
  postDuePeriods: (data) => api.post('/leases/post-due-periods', data),
  deleteLease: (id) => api.delete(`/leases/${id}`),
};

// Installment Sales API (IFRS 15 / IFRS 9)
export const installmentSalesAPI = {
  getNextNumber: () => api.get('/installment-sales/next-number'),
  getSummary: () => api.get('/installment-sales/summary'),
  previewSchedule: (data) => api.post('/installment-sales/preview-schedule', data),
  getAll: (params) => api.get('/installment-sales', { params }),
  getById: (id) => api.get(`/installment-sales/${id}`),
  create: (data) => api.post('/installment-sales', data),
  update: (id, data) => api.put(`/installment-sales/${id}`, data),
  postCustomerAdvance: (id, data) => api.post(`/installment-sales/${id}/customer-advance`, data),
  postControlTransfer: (id, data) => api.post(`/installment-sales/${id}/control-transfer`, data),
  recordPayment: (id, data) => api.post(`/installment-sales/${id}/payments`, data),
  accruePeriod: (id, data) => api.post(`/installment-sales/${id}/accrue-period`, data),
  runBatchAccruals: (data) => api.post('/installment-sales/batch-accruals', data),
  getSettlementQuote: (id, params) => api.get(`/installment-sales/${id}/settlement-quote`, { params }),
  settleEarly: (id, data) => api.post(`/installment-sales/${id}/settle-early`, data),
  processRepossession: (id, data) => api.post(`/installment-sales/${id}/repossess`, data),
  evaluateECL: (id, data) => api.post(`/installment-sales/${id}/evaluate-ecl`, data),
  submitForApproval: (id, data) => api.post(`/installment-sales/${id}/submit-approval`, data),
  approve: (id, data) => api.post(`/installment-sales/${id}/approve`, data),
  reject: (id, data) => api.post(`/installment-sales/${id}/reject`, data),
};

// Accounting Audit Trail API (used by AuditTrailDialog)
export const accountingAuditAPI = {
  getLogs: (params) => api.get('/accounting-audit', { params }),
  getLogDetail: (id) => api.get(`/accounting-audit/${id}`),
  verifyChain: (entityType) => api.post('/accounting-audit/verify-chain', { entityType }),
  getById: (id) => api.get(`/accounting-audit/${id}`),
};


// Generic Approval Workflow API
export const approvalAPI = {
  submit: (entityType, entityId, data) => api.post(`/approvals/submit`, { entityType, entityId, ...data }),
  approve: (approvalId, data) => api.post(`/approvals/${approvalId}/approve`, data),
  reject: (approvalId, data) => api.post(`/approvals/${approvalId}/reject`, data),
  getForEntity: (entityType, entityId) => api.get('/approvals', { params: { entityType, entityId } }),
};

// Stock Adjustment approval helpers (delegates to inventory routes)
export const stockAdjustmentApprovalAPI = {
  submitForApproval: (id, data) => api.post(`/inventory/adjustments/${id}/submit-approval`, data),
  approve: (id, data) => api.post(`/inventory/adjustments/${id}/approve`, data),
  reject: (id, data) => api.post(`/inventory/adjustments/${id}/reject`, data),
};

export default api;
